import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Alert, Box, ButtonBase, MenuItem, Stack, TextField, Typography} from 'views/components';
import {ExpandMore} from 'views/icons';
import {AdminAutocomplete, translate, Translate} from 'views/module';

import getAutomationRules from '../../../util/automationRules';

const AutomationsStepRules = (props) => {
  const {
    automationForm,
    initAssigneesInfo,
    isTriggerLocked,
    setAssigneeRns,
    setPlaybookRn,
    setPrompt,
    setResourceType,
    setUpdateRuleField,
  } = props;
  const playbooks = props.playbooks;
  const [selectedAdminOption, setSelectedAdminOption] = React.useState(initAssigneesInfo?.[0] || null);
  const [listVisible, setListVisible] = React.useState(false);
  const [commandQuery, setCommandQuery] = React.useState('');
  const [highlightedOptionIndex, setHighlightedOptionIndex] = React.useState(0);
  const [trackedCommandStart, setTrackedCommandStart] = React.useState(null);

  const skipCommandInputRef = React.useRef(false);
  const hasInitializedPlaybookPromptRef = React.useRef(false);
  const isHydratingPlaybookPromptRef = React.useRef(false);
  const promptFieldRef = React.useRef(null);
  const promptHighlightRef = React.useRef(null);

  const automationRules = React.useMemo(() => getAutomationRules(), []);
  const escapeRegExp = React.useCallback((value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), []);
  const sortedPlaybooks = React.useMemo(() => {
    return [...(Array.isArray(playbooks) ? playbooks : [])]
        .sort((left, right) => (left?.title || '').localeCompare(right?.title || ''));
  }, [playbooks]);

  React.useEffect(() => {
    if (automationForm.assigneeRns.length === 0) {
      setSelectedAdminOption(null);
    }
  }, [automationForm.assigneeRns]);

  React.useEffect(() => {
    if (!initAssigneesInfo) {
      return;
    }

    setSelectedAdminOption(initAssigneesInfo[0] || null);
  }, [initAssigneesInfo]);

  const handleSelectedAdminChange = React.useCallback((nextSelectedAdmin) => {
    const nextAssigneeRn = nextSelectedAdmin?.userRn;

    setSelectedAdminOption(nextSelectedAdmin);
    setAssigneeRns(nextAssigneeRn ? [nextAssigneeRn] : []);
  }, [setAssigneeRns]);

  const closeCommandMenu = React.useCallback(() => {
    setListVisible(false);
    setCommandQuery('');
    setTrackedCommandStart(null);
    setHighlightedOptionIndex(0);
  }, []);

  const filteredPlaybooks = React.useMemo(() => {
    const query = commandQuery.toLowerCase();

    return sortedPlaybooks.filter((playbook) => {
      const playbookTitle = (playbook?.title || '').toLowerCase();
      const playbookKey = (playbook?.playbookRn || '').toLowerCase();

      return playbookTitle.includes(query) || playbookKey.includes(query);
    });
  }, [commandQuery, sortedPlaybooks]);

  const selectedPlaybook = React.useMemo(() => {
    return sortedPlaybooks.find((playbook) => playbook.playbookRn === automationForm.playbookRn) || null;
  }, [automationForm.playbookRn, sortedPlaybooks]);

  const insertPlaybook = React.useCallback((playbook) => {
    if (trackedCommandStart === null) {
      return;
    }

    const playbookTitle = playbook?.title || '';
    const currentPlaybookMatch = /\/[^/\s]*$/.exec(automationForm.prompt.slice(trackedCommandStart));
    const playbookLength = currentPlaybookMatch?.[0]?.length || 0;
    const nextPrompt = `${automationForm.prompt.slice(0, trackedCommandStart)}/${playbookTitle}${
      automationForm.prompt.slice(trackedCommandStart + playbookLength)
    }`;

    skipCommandInputRef.current = true;
    setPrompt(nextPrompt);
    setPlaybookRn(playbook.playbookRn);
    closeCommandMenu();
  }, [automationForm.prompt, closeCommandMenu, setPlaybookRn, setPrompt, trackedCommandStart]);

  const handlePromptKeyDown = React.useCallback((event) => {
    if (!listVisible) {
      return;
    }

    if (event.key === 'Escape') {
      skipCommandInputRef.current = true;
      event.preventDefault();
      closeCommandMenu();
      return;
    }

    if (filteredPlaybooks.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      skipCommandInputRef.current = true;
      event.preventDefault();
      setHighlightedOptionIndex((prev) => (prev + 1) % filteredPlaybooks.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      skipCommandInputRef.current = true;
      event.preventDefault();
      setHighlightedOptionIndex((prev) => (prev - 1 + filteredPlaybooks.length) % filteredPlaybooks.length);
      return;
    }

    if (event.key === 'Enter' || event.key === 'Tab') {
      const nextPlaybook = filteredPlaybooks[highlightedOptionIndex] || filteredPlaybooks[0];

      if (nextPlaybook) {
        skipCommandInputRef.current = true;
        event.preventDefault();
        insertPlaybook(nextPlaybook);
      }
    }
  }, [closeCommandMenu, filteredPlaybooks, highlightedOptionIndex, insertPlaybook, listVisible]);

  const selectedRule = React.useMemo(() => {
    return automationRules.find((rule) => rule.resourceType === automationForm.resourceType) || automationRules[0];
  }, [automationForm.resourceType]);

  const hasUpdateRules = automationForm.updateRules.length > 0;

  const defaultVisibleRequiredFields = React.useMemo(() => {
    return selectedRule.fields.filter((field) => {
      return Boolean(field.isVisibleToAdmin) && Boolean(field.isRequired);
    });
  }, [selectedRule.fields]);

  const defaultStringEqualFieldForSelectedResource = React.useMemo(() => {
    return defaultVisibleRequiredFields.find((field) => field.ruleType === 'STRING_EQUAL') || null;
  }, [defaultVisibleRequiredFields]);

  const defaultPromptField = React.useMemo(() => {
    return defaultVisibleRequiredFields.find((field) => field.ruleType === 'AI_PROMPT') || null;
  }, [defaultVisibleRequiredFields]);

  const stringEqualField = React.useMemo(() => {
    if (isTriggerLocked) {
      return null;
    }

    if (!hasUpdateRules) {
      return defaultStringEqualFieldForSelectedResource;
    }

    const matchingUpdateRule = automationForm.updateRules.find((updateRule) => {
      return updateRule.ruleType === 'STRING_EQUAL';
    });

    if (!matchingUpdateRule) {
      return null;
    }

    return selectedRule.fields.find((field) => {
      return (
        Boolean(field.isVisibleToAdmin) &&
        field.ruleType === 'STRING_EQUAL' &&
        field.key === matchingUpdateRule.stringEqualCheck.key
      );
    });
  }, [automationForm.updateRules, defaultStringEqualFieldForSelectedResource, hasUpdateRules, isTriggerLocked, selectedRule.fields]);

  const promptField = React.useMemo(() => {
    if (isTriggerLocked) {
      return null;
    }

    if (!hasUpdateRules) {
      return defaultPromptField;
    }

    const matchingUpdateRule = automationForm.updateRules.find((updateRule) => {
      return updateRule.ruleType === 'AI_PROMPT';
    });

    if (!matchingUpdateRule) {
      return null;
    }

    return selectedRule.fields.find((field) => {
      return (
        Boolean(field.isVisibleToAdmin) &&
        field.ruleType === 'AI_PROMPT' &&
        field.key === matchingUpdateRule.promptCheck.key
      );
    });
  }, [automationForm.updateRules, defaultPromptField, hasUpdateRules, isTriggerLocked, selectedRule.fields]);

  const valueOptions = React.useMemo(() => {
    if (!stringEqualField) {
      return [];
    }

    return stringEqualField.options || [];
  }, [stringEqualField]);

  const selectedValue = React.useMemo(() => {
    if (isTriggerLocked) {
      return '';
    }

    if (!stringEqualField) {
      return '';
    }

    if (!hasUpdateRules) {
      if (stringEqualField.defaultValue) {
        return stringEqualField.defaultValue;
      }

      if (valueOptions.length === 0) {
        return '';
      }

      return valueOptions[0].value;
    }

    const matchingUpdateRule = automationForm.updateRules.find((updateRule) => {
      return updateRule.ruleType === 'STRING_EQUAL' && updateRule.stringEqualCheck.key === stringEqualField.key;
    });

    return matchingUpdateRule.stringEqualCheck.value;
  }, [automationForm.updateRules, hasUpdateRules, isTriggerLocked, stringEqualField, valueOptions]);

  const selectedAiPrompt = React.useMemo(() => {
    if (isTriggerLocked) {
      return '';
    }

    if (!promptField) {
      return '';
    }

    if (!hasUpdateRules) {
      return '';
    }

    const matchingUpdateRule = automationForm.updateRules.find((updateRule) => {
      return updateRule.ruleType === 'AI_PROMPT' && updateRule.promptCheck.key === promptField.key;
    });

    return matchingUpdateRule.promptCheck.prompt;
  }, [automationForm.updateRules, hasUpdateRules, isTriggerLocked, promptField]);

  React.useEffect(() => {
    setHighlightedOptionIndex(0);
  }, [commandQuery]);

  React.useEffect(() => {
    if (skipCommandInputRef.current) {
      skipCommandInputRef.current = false;
      return;
    }

    const playbookMatch = /(^|\s)\/([^/\s]*)$/.exec(automationForm.prompt);

    if (!playbookMatch) {
      closeCommandMenu();
      return;
    }

    const commandStart = playbookMatch.index + playbookMatch[1].length;

    setTrackedCommandStart(commandStart);
    setListVisible(true);
    setCommandQuery((playbookMatch[2] || '').trimStart().toLowerCase());
  }, [automationForm.prompt, closeCommandMenu]);

  React.useEffect(() => {
    if (hasInitializedPlaybookPromptRef.current) {
      return;
    }

    if (!selectedPlaybook?.title) {
      hasInitializedPlaybookPromptRef.current = true;
      return;
    }

    hasInitializedPlaybookPromptRef.current = true;
    if (automationForm.prompt.includes(`/${selectedPlaybook.title}`)) {
      return;
    }

    isHydratingPlaybookPromptRef.current = true;
    setPrompt(`/${selectedPlaybook.title} ${automationForm.prompt}`.trim());
  }, [automationForm.prompt, selectedPlaybook, setPrompt]);

  React.useEffect(() => {
    if (!selectedPlaybook?.title) {
      return;
    }

    if (isHydratingPlaybookPromptRef.current) {
      if (automationForm.prompt.includes(`/${selectedPlaybook.title}`)) {
        isHydratingPlaybookPromptRef.current = false;
      }
      return;
    }

    if (automationForm.prompt.includes(`/${selectedPlaybook.title}`)) {
      return;
    }

    setPlaybookRn(null);
  }, [automationForm.prompt, selectedPlaybook, setPlaybookRn]);

  React.useEffect(() => {
    const inputElement = promptFieldRef.current?.querySelector('textarea');
    const highlightElement = promptHighlightRef.current;

    if (!inputElement || !highlightElement) {
      return undefined;
    }

    const syncHighlightScroll = () => {
      highlightElement.style.transform = `translate(${-inputElement.scrollLeft}px, ${-inputElement.scrollTop}px)`;
    };

    syncHighlightScroll();
    inputElement.addEventListener('scroll', syncHighlightScroll);

    return () => {
      inputElement.removeEventListener('scroll', syncHighlightScroll);
    };
  }, [automationForm.prompt]);

  const renderHighlightedPrompt = () => {
    if (!automationForm.prompt) {
      return null;
    }

    const highlightRanges = [];

    if (selectedPlaybook?.title) {
      const playbookMatcher = new RegExp(`/${escapeRegExp(selectedPlaybook.title)}`, 'g');
      let playbookMatch = playbookMatcher.exec(automationForm.prompt);

      while (playbookMatch) {
        highlightRanges.push({
          start: playbookMatch.index,
          end: playbookMatch.index + playbookMatch[0].length,
          type: 'playbook',
        });
        playbookMatch = playbookMatcher.exec(automationForm.prompt);
      }
    } else {
      const currentPlaybookMatch = /(^|\s)(\/[^/\s]*)$/.exec(automationForm.prompt);

      if (currentPlaybookMatch) {
        const playbookValue = currentPlaybookMatch[2];
        const playbookStart = currentPlaybookMatch.index + currentPlaybookMatch[1].length;

        highlightRanges.push({
          start: playbookStart,
          end: playbookStart + playbookValue.length,
          type: 'playbook',
        });
      }
    }

    highlightRanges.sort((left, right) => left.start - right.start);

    const segments = [];
    let cursor = 0;

    highlightRanges.forEach((range, index) => {
      if (range.start < cursor) {
        return;
      }

      if (range.start > cursor) {
        segments.push(
            <Box
              component='span'
              key={`text-${cursor}`}
              sx={{color: '#1A1916'}}
            >
              {automationForm.prompt.slice(cursor, range.start)}
            </Box>,
        );
      }

      segments.push(
          <Box
            component='span'
            key={`${range.type}-${range.start}-${index}`}
            sx={{
              color: '#DA7955',
            }}
          >
            {automationForm.prompt.slice(range.start, range.end)}
          </Box>,
      );

      cursor = range.end;
    });

    if (cursor < automationForm.prompt.length) {
      segments.push(
          <Box
            component='span'
            key={`text-${cursor}-end`}
            sx={{color: '#1A1916'}}
          >
            {automationForm.prompt.slice(cursor)}
          </Box>,
      );
    }

    return segments;
  };

  return (
    <Stack
      sx={{
        px: {
          xs: '20px',
          sm: '26px',
        },
        py: '18px',
        gap: '20px',
      }}
    >
      <Stack
        sx={{
          gap: '16px',
          p: '20px',
          border: '1px solid #E8E4DE',
          borderRadius: '12px',
          backgroundColor: '#FBFBFB',
        }}
      >
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            color: '#A8A49E',
            lineHeight: '100%',
            textTransform: 'uppercase',
          }}
        >
          <Translate expression='automations.Automations.TriggerConditions.Title' />
        </Typography>
        <Stack
          sx={{
            gap: '10px',
          }}
        >
          {
            isTriggerLocked &&
            <Alert severity='warning'>
              <Translate expression='automations.Automations.TriggerConditions.UnsupportedRuleMessage' />
            </Alert>
          }
          {
            !isTriggerLocked &&
            <Stack
              direction='row'
              alignItems='center'
              flexWrap='wrap'
              sx={{
                gap: '10px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#1A1916',
                  lineHeight: '150%',
                }}
              >
                <Translate expression='automations.Automations.New.Steps.Rules.TriggerConditions.EventResourcePrepend' />
              </Typography>
              <TextField
                select
                value={automationForm.resourceType}
                onChange={setResourceType}
                SelectProps={{
                  IconComponent: ExpandMore,
                  renderValue: (value) => {
                    return automationRules.find((rule) => rule.resourceType === value).resourceTypeLabel;
                  },
                }}
                sx={{
                  'width': 'fit-content',
                  'maxWidth': '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#FFFFFF',
                  },
                  '& .MuiSelect-select': {
                    pl: '16px',
                    pr: '24px',
                    py: '12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '150%',
                    color: '#DA7955',
                    boxSizing: 'border-box',
                  },
                  '& .MuiSelect-icon': {
                    color: '#DA7955',
                    right: '12px',
                  },
                }}
              >
                {
                  automationRules.map((rule) => (
                    <MenuItem
                      key={rule.resourceType}
                      value={rule.resourceType}
                    >
                      {rule.resourceTypeLabel}
                    </MenuItem>
                  ))
                }
              </TextField>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#1A1916',
                  lineHeight: '150%',
                }}
              >
                {selectedRule.sentenceConnector}
              </Typography>
              {
                stringEqualField &&
                <TextField
                  select
                  value={selectedValue}
                  onChange={(nextValue) => setUpdateRuleField(stringEqualField, nextValue)}
                  SelectProps={{
                    IconComponent: ExpandMore,
                    renderValue: (value) => {
                      return valueOptions.find((option) => option.value === value)?.label || value;
                    },
                  }}
                  sx={{
                    'width': 'fit-content',
                    'maxWidth': '100%',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                    },
                    '& .MuiSelect-select': {
                      pl: '16px',
                      pr: '24px',
                      py: '12px',
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '150%',
                      color: '#DA7955',
                      boxSizing: 'border-box',
                    },
                    '& .MuiSelect-icon': {
                      color: '#DA7955',
                      right: '12px',
                    },
                  }}
                >
                  {
                    valueOptions.map((valueOption) => (
                      <MenuItem
                        key={valueOption.value}
                        value={valueOption.value}
                      >
                        {valueOption.label}
                      </MenuItem>
                    ))
                  }
                </TextField>
              }
            </Stack>
          }
        </Stack>
        {
          !isTriggerLocked && promptField &&
          <Stack sx={{gap: '8px'}}>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#A8A49E',
                lineHeight: '100%',
                textTransform: 'uppercase',
              }}
            >
              {promptField.label}
            </Typography>
            <TextField
              value={selectedAiPrompt}
              onChange={(nextValue) => setUpdateRuleField(promptField, nextValue)}
              placeholder={translate('automations.Automations.New.Steps.Rules.AiEvaluatedRulePlaceholder')}
              multiline
              minRows={4}
              maxRows={8}
              sx={{
                '& .MuiOutlinedInput-root': {
                  'borderRadius': '12px',
                  'backgroundColor': '#FFFFFF',
                  'alignItems': 'flex-start',
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '150%',
                  color: '#1A1916',
                  boxSizing: 'border-box',
                },
                '& .MuiOutlinedInput-input::placeholder': {
                  color: '#A8A49E',
                  opacity: 1,
                  whiteSpace: 'pre-line',
                },
              }}
            />
          </Stack>
        }
      </Stack>

      <Stack sx={{gap: '8px'}}>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#A8A49E',
            lineHeight: '100%',
            textTransform: 'uppercase',
          }}
        >
          <Translate expression='automations.Automations.New.Steps.Rules.PromptTitle' />
        </Typography>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
          }}
        >
          {
            !!automationForm.prompt &&
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                px: '14px',
                py: '16.5px',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '150%',
                whiteSpace: 'pre-wrap',
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            >
              <Box
                ref={promptHighlightRef}
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '150%',
                  fontFamily: 'inherit',
                  letterSpacing: 'inherit',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {renderHighlightedPrompt()}
              </Box>
            </Box>
          }
          <Box ref={promptFieldRef}>
            <TextField
              value={automationForm.prompt}
              onChange={setPrompt}
              onKeyDown={handlePromptKeyDown}
              onBlur={() => {
                window.setTimeout(() => {
                  closeCommandMenu();
                }, 100);
              }}
              placeholder={translate('automations.Automations.New.Steps.Rules.PromptPlaceholder')}
              multiline
              minRows={5}
              maxRows={10}
              sx={{
                'width': '100%',
                '& .MuiOutlinedInput-root': {
                  'borderRadius': '12px',
                  'backgroundColor': 'transparent',
                  'alignItems': 'flex-start',
                  'position': 'relative',
                  'zIndex': 2,
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '150%',
                  color: 'transparent',
                  caretColor: '#1A1916',
                  WebkitTextFillColor: 'transparent',
                  boxSizing: 'border-box',
                  position: 'relative',
                  zIndex: 2,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  backgroundColor: 'transparent',
                },
                '& textarea': {
                  overflowY: 'auto !important',
                },
                '& .MuiOutlinedInput-input::placeholder': {
                  color: '#A8A49E',
                  opacity: 1,
                  whiteSpace: 'pre-line',
                },
              }}
            />
          </Box>
          {
            listVisible &&
            <Box
              sx={{
                position: 'absolute',
                top: 'calc(100% + 1px)',
                left: 0,
                right: 0,
                maxHeight: '240px',
                overflowY: 'auto',
                border: '1px solid #E8E4DE',
                borderRadius: '12px',
                backgroundColor: '#FFFFFF',
                zIndex: 10,
              }}
            >
              {
                filteredPlaybooks.length === 0 &&
                <Typography
                  sx={{
                    px: '12px',
                    py: '10px',
                    fontSize: '12px',
                    color: '#6B6760',
                  }}
                >
                  <Translate expression='automations.Automations.New.Steps.Rules.PlaybookNoMatches' />
                </Typography>
              }
              {
                filteredPlaybooks.map((playbook, index) => {
                  const isHighlighted = index === highlightedOptionIndex;

                  return (
                    <ButtonBase
                      key={playbook.playbookRn}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        insertPlaybook(playbook);
                      }}
                      sx={{
                        'width': '100%',
                        'justifyContent': 'flex-start',
                        'px': '12px',
                        'py': '10px',
                        'backgroundColor': isHighlighted ? '#F3F1EE' : '#FFFFFF',
                        '&:hover': {
                          backgroundColor: '#F8F6F3',
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: '#1A1916',
                          lineHeight: '150%',
                        }}
                      >
                        /{playbook.title}
                      </Typography>
                    </ButtonBase>
                  );
                })
              }
            </Box>
          }
        </Box>
        <Typography
          sx={{
            color: '#A8A49E',
            fontSize: '13px',
            lineHeight: '100%',
          }}
        >
          <Translate expression='automations.Automations.New.Steps.Rules.PromptFooter' />
        </Typography>
        {
          selectedPlaybook &&
          <Typography
            sx={{
              color: '#6B6760',
              fontSize: '12px',
              lineHeight: '100%',
            }}
          >
            <Translate
              expression='automations.Automations.New.Steps.Rules.SelectedPlaybook'
              substitute={{title: `/${selectedPlaybook.title}`}}
            />
          </Typography>
        }
      </Stack>

      <Stack sx={{gap: '8px'}}>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#A8A49E',
            lineHeight: '100%',
            textTransform: 'uppercase',
          }}
        >
          <Translate expression='automations.Automations.New.Steps.Rules.AssignedToTitle' />
        </Typography>
        <AdminAutocomplete
          selectedAdmin={selectedAdminOption}
          setSelectedAdmin={handleSelectedAdminChange}
        />
      </Stack>
    </Stack>
  );
};

AutomationsStepRules.propTypes = {
  automationForm: PropTypes.shape({
    assigneeRns: PropTypes.arrayOf(PropTypes.string).isRequired,
    playbookRn: PropTypes.string,
    prompt: PropTypes.string.isRequired,
    resourceType: PropTypes.string.isRequired,
    updateRules: PropTypes.array.isRequired,
  }).isRequired,
  isTriggerLocked: PropTypes.bool.isRequired,
  setAssigneeRns: PropTypes.func.isRequired,
  setPlaybookRn: PropTypes.func.isRequired,
  setPrompt: PropTypes.func.isRequired,
  setResourceType: PropTypes.func.isRequired,
  setUpdateRuleField: PropTypes.func.isRequired,
  initAssigneesInfo: PropTypes.array,
  playbooks: PropTypes.array.isRequired,
};

const mapStateToProps = (state) => ({
  playbooks: state.playbooks.allPlaybooks,
});

export default connect(mapStateToProps, dispatch)(React.memo(AutomationsStepRules));
